/**
 * @typedef {'contractors'|string} ExpenseSectionName
 */

const applyNetAmountToRow = ({Vue, row, value, tracksAssignable}) => {
   Vue.set(row, 'totalDebit', value);
   if (tracksAssignable) {
      Vue.set(row, 'assignable', value !== null);
   }
};

const rollbackNetAmountToRow = ({Vue, row, snapshot, tracksAssignable}) => {
   Vue.set(row, 'totalDebit', snapshot.totalDebit);
   if (tracksAssignable) {
      Vue.set(row, 'assignable', snapshot.assignable);
   }
};

const SECTION_STRATEGIES = Object.freeze({
   default: {
      normalizeManualVendor: ({vendor}) => vendor,
      buildManualVendorPayload: ({payload, normalizedLocation, manualVendorDefaults}) => ({
         ...manualVendorDefaults,
         ...payload,
         companyId: payload.companyId,
         name: payload.name,
         location: normalizedLocation,
      }),
      buildManualSummaryPeriod: ({basePeriod, manualVendor}) => ({
         ...basePeriod,
         vendorId: manualVendor.id,
         totalDebit: null,
      }),
      buildDummySummary: ({baseSummary}) => baseSummary,
      applyNetAmountToRow: (params) => applyNetAmountToRow({...params, tracksAssignable: false}),
      rollbackNetAmountToRow: (params) =>
         rollbackNetAmountToRow({...params, tracksAssignable: false}),
   },
   contractors: {
      normalizeManualVendor: ({vendor, manualVendorDefaults}) => {
         const source = vendor.contractor || vendor;
         const id = source.id ?? vendor.id;
         const companyId = source.companyId ?? vendor.companyId;
         const studyId = source.studyId ?? vendor.studyId;
         const location = source.location ?? vendor.location ?? null;
         const name = source.name ?? vendor.name ?? null;

         return {
            ...manualVendorDefaults,
            ...vendor,
            ...source,
            id,
            companyId,
            studyId,
            location,
            name,
            description: source.description ?? null,
            contractType: source.contractType ?? null,
            paymentType: source.paymentType ?? source.contractType ?? null,
            contractOptOut: source.contractOptOut ?? false,
            companyIpRights: source.companyIpRights ?? null,
            economicRisk: source.economicRisk ?? null,
         };
      },
      buildManualVendorPayload: ({payload, normalizedLocation, rootGetters}) => {
         const studyId = rootGetters['companies/activeStudyId'];
         const name = payload.name || null;
         const contractType = payload.contractType ?? payload.paymentType ?? null;
         return {
            companyId: payload.companyId,
            studyId,
            name,
            location: normalizedLocation,
            description: payload.description ?? null,
            contractType,
            paymentType: contractType,
            contractOptOut: payload.contractOptOut ?? false,
            companyIpRights: payload.companyIpRights ?? null,
            economicRisk: payload.economicRisk ?? null,
         };
      },
      buildManualSummaryPeriod: ({basePeriod, manualVendor}) => ({
         ...basePeriod,
         contractorId: manualVendor.id,
         totalDebit: null,
         assignable: false,
      }),
      buildDummySummary: ({baseSummary, vendor}) => ({
         ...baseSummary,
         vendorId: null,
         contractorId: vendor.id,
         assignable: false,
      }),
      applyNetAmountToRow: (params) => applyNetAmountToRow({...params, tracksAssignable: true}),
      rollbackNetAmountToRow: (params) =>
         rollbackNetAmountToRow({...params, tracksAssignable: true}),
   },
   employees: {
      normalizeManualVendor: ({vendor}) => ({
         id: vendor.id,
         companyId: vendor.companyId,
         studyId: vendor.studyId,
         ident: vendor.ident ?? null,
         fullname: vendor.fullname ?? vendor.name ?? null,
         name: vendor.fullname ?? vendor.name ?? null,
         title: vendor.title ?? null,
         department: vendor.department ?? null,
         subsidiary: vendor.subsidiary ?? null,
         location: vendor.location ?? null,
         officer: vendor.officer ?? false,
         payroll: vendor.payroll ?? null,
         assigneeId: vendor.assigneeId ?? null,
      }),
      // EmployeeCreate has no `studyId` field on the server — unlike contractors,
      // it must not be sent. Fields are listed explicitly rather than spread from
      // MANUAL_VENDOR_DEFAULTS, which is shaped for the generic/contractor forms.
      buildManualVendorPayload: ({payload, normalizedLocation}) => ({
         companyId: payload.companyId,
         ident: payload.ident ?? null,
         fullname: payload.fullname ?? payload.name ?? null,
         title: payload.title ?? null,
         department: payload.department ?? null,
         subsidiary: payload.subsidiary ?? null,
         location: normalizedLocation,
         officer: payload.officer ?? false,
         payroll: payload.payroll ?? null,
      }),
      buildManualSummaryPeriod: ({basePeriod, manualVendor}) => ({
         ...basePeriod,
         employeeId: manualVendor.id,
         totalDebit: null,
      }),
      buildDummySummary: ({baseSummary}) => baseSummary,
      applyNetAmountToRow: (params) => applyNetAmountToRow({...params, tracksAssignable: false}),
      rollbackNetAmountToRow: (params) =>
         rollbackNetAmountToRow({...params, tracksAssignable: false}),
   },
});

const getSectionStrategy = (sectionName) =>
   SECTION_STRATEGIES[sectionName] || SECTION_STRATEGIES.default;

/**
 * Normalizes manual vendor API data according to section-specific rules.
 * @param {{sectionName: ExpenseSectionName, vendor: Object|null|undefined, manualVendorDefaults: Object}} params
 * @returns {Object|null}
 */
export const normalizeManualVendorBySection = ({sectionName, vendor, manualVendorDefaults}) => {
   if (!vendor) return null;
   return getSectionStrategy(sectionName).normalizeManualVendor({vendor, manualVendorDefaults});
};

/**
 * Builds manual vendor create/update payload according to section-specific rules.
 * @param {{
 *   sectionName: ExpenseSectionName,
 *   payload: Object,
 *   getters: Object,
 *   rootGetters: Object,
 *   manualVendorDefaults: Object
 * }} params
 * @returns {Object}
 */
export const buildManualVendorPayloadBySection = ({
   sectionName,
   payload,
   getters,
   rootGetters,
   manualVendorDefaults,
}) => {
   const normalizedLocation = payload.location || getters.location;
   return getSectionStrategy(sectionName).buildManualVendorPayload({
      payload,
      normalizedLocation,
      rootGetters,
      manualVendorDefaults,
   });
};

/**
 * Builds one manual summary-period payload entry for the given section.
 * @param {{sectionName: ExpenseSectionName, basePeriod: Object, manualVendor: Object}} params
 * @returns {Object}
 */
export const buildManualSummaryPeriodBySection = ({sectionName, basePeriod, manualVendor}) =>
   getSectionStrategy(sectionName).buildManualSummaryPeriod({basePeriod, manualVendor});

/**
 * Builds section-specific dummy summary seed fields.
 * @param {{sectionName: ExpenseSectionName, baseSummary: Object, vendor: Object}} params
 * @returns {Object}
 */
export const buildDummySummaryBySection = ({sectionName, baseSummary, vendor}) =>
   getSectionStrategy(sectionName).buildDummySummary({baseSummary, vendor});

/**
 * Applies net amount to a summary row using section-specific target fields.
 * @param {{sectionName: ExpenseSectionName, Vue: Object, row: Object, value: number|null}} params
 * @returns {void}
 */
export const applyNetAmountToRowBySection = ({sectionName, Vue, row, value}) =>
   getSectionStrategy(sectionName).applyNetAmountToRow({Vue, row, value});

/**
 * Rolls back net amount fields on a summary row using section-specific rules.
 * @param {{sectionName: ExpenseSectionName, Vue: Object, row: Object, snapshot: Object}} params
 * @returns {void}
 */
export const rollbackNetAmountToRowBySection = ({sectionName, Vue, row, snapshot}) =>
   getSectionStrategy(sectionName).rollbackNetAmountToRow({Vue, row, snapshot});
